import get from 'lodash/get';

/** @typedef {'Active'|'PendingAgreement'|'PendingActivation'|'Inactive'|'PendingRenewal'|'Deactivated'|'Cancelled'} ClientStatus */

export const CLIENT_STATUS_CONFIG = {
  Active: { label: 'Active', className: 'bg-success-bg text-success', usable: true },
  PendingActivation: { label: 'Awaiting setup', className: 'bg-warning-bg text-warning', usable: false },
  PendingAgreement: { label: 'Awaiting agreement', className: 'bg-warning-bg text-warning', usable: false },
  Inactive: { label: 'Paused', className: 'bg-muted text-muted-foreground', usable: false },
  PendingRenewal: { label: 'Renewal due', className: 'bg-warning-bg text-warning', usable: false },
  Deactivated: { label: 'Ended', className: 'bg-muted text-muted-foreground', usable: false },
  Cancelled: { label: 'Cancelled', className: 'bg-danger-bg text-danger', usable: false },
};

const LEGACY_STATUS_MAP = {
  active: 'Active',
  pending_agreement: 'PendingAgreement',
  pending_activation: 'PendingActivation',
  inactive: 'Inactive',
  pending_renewal: 'PendingRenewal',
  deactivated: 'Deactivated',
  cancelled: 'Cancelled',
};

/**
 * Normalise org lifecycle status (prefer PascalCase `status`, fall back to legacy `organisationStatus`).
 * @param {object} org
 * @returns {string|undefined}
 */
export const resolveClientStatus = (org) => {
  if (!org) return undefined;
  if (org.status) return org.status;
  const legacy = org.organisationStatus;
  if (legacy && LEGACY_STATUS_MAP[legacy]) return LEGACY_STATUS_MAP[legacy];
  if (org.active === false) return 'Inactive';
  if (org.active === true) return 'Active';
  return undefined;
};

/**
 * Mirror backend isClientUsable() for consultant client orgs.
 * @param {object} org
 * @param {{ billingStatus?: string }} [options]
 * @returns {boolean}
 */
export const isClientUsable = (org, options = {}) => {
  if (!org) return false;

  const status = resolveClientStatus(org);
  if (status !== 'Active') return false;
  if (org.reportGeneration === false) return false;

  const relationship = org.billingRelationship;

  if (relationship === 'direct_billed') {
    return org.hasSubscriptionActive === true;
  }

  if (relationship === 'consultant_billed') {
    const billingStatus = options.billingStatus ?? org.billingStatus;
    if (billingStatus && billingStatus !== 'current') return false;
    return true;
  }

  return true;
};

/**
 * Human-readable reason when a client cannot generate reports.
 * @param {object} org
 * @returns {string|null}
 */
export const getClientUnusableReason = (org) => {
  if (!org) return 'Client organisation not found.';

  const status = resolveClientStatus(org);
  const statusCfg = CLIENT_STATUS_CONFIG[status];

  if (status && status !== 'Active') {
    return statusCfg?.label
      ? `This client is ${statusCfg.label.toLowerCase()} and cannot generate reports yet.`
      : 'This client account is not active for report generation.';
  }

  if (org.reportGeneration === false) {
    return 'Report generation is disabled for this client.';
  }

  if (org.billingRelationship === 'direct_billed' && org.hasSubscriptionActive !== true) {
    return 'The client has not activated their subscription yet. Facilities, usage, and reports stay locked until they accept the order form and complete payment.';
  }

  if (org.billingRelationship === 'consultant_billed' && org.billingStatus && org.billingStatus !== 'current') {
    return 'This client billing is not current.';
  }

  return null;
};

/**
 * @param {string|undefined} relationship
 * @returns {string}
 */
export const billingRelationshipLabel = (relationship) => {
  if (relationship === 'direct_billed') return 'Client subscription';
  if (relationship === 'consultant_billed') return 'Billed to you';
  return 'Legacy consultant';
};

export const statusBadgeConfig = (org) => {
  const status = resolveClientStatus(org) || 'Active';
  return CLIENT_STATUS_CONFIG[status] || { label: status, className: 'bg-muted text-muted-foreground', usable: false };
};

export const isConsultantClientOrg = (org, consultantParentId) => {
  const consultantId = get(org, 'consultantId');
  if (!consultantId || consultantId === 'NONE') return false;
  if (consultantParentId && org.id === consultantParentId) return false;
  return org.id !== consultantId;
};

/**
 * Copy shown when a consultant end-client workspace is locked (no usable subscription).
 * Covers both billing paths so consultants are not left guessing why Facilities/Usage are disabled.
 * @param {object} org
 * @returns {string|null}
 */
export const getConsultantClientAccessLockMessage = (org) => {
  if (!isConsultantClientOrg(org)) return null;
  if (org.hasSubscriptionActive === true) return null;

  if (org.billingRelationship === 'consultant_billed') {
    return 'This client organisation is not activated yet. Open Client Portfolio and choose Activate before adding facilities or usage data.';
  }

  return 'This client pays via their own subscription. Facilities and usage stay locked until they accept the order form and complete payment. Resend the invitation from Client Portfolio if they have not received it.';
};

export const filterConsultantClients = (orgs, consultantParentId) =>
  (orgs || []).filter((org) => isConsultantClientOrg(org, consultantParentId));

export const CONSULTANT_CLIENT_ERROR_MESSAGES = {
  not_consultant_admin: 'You do not have consultant admin access.',
  invalid_billing_relationship: 'Please choose how this client will be billed.',
  invalid_plan: 'Select a plan type and pricing for this client.',
  consultant_master_contract_missing:
    'Your consultant agreement is not complete. Contact Compare Your Footprint to finish setup before adding clients.',
  agreements_flow_disabled: 'Direct client billing is not available yet. Use consultant-billed for now.',
  price_not_in_allowlist: 'The selected plan is not available for direct client billing.',
  client_already_active: 'This client is already active — resend and void are not available.',
  not_consultant_billed: 'This action only applies to consultant-billed clients.',
  schedule_not_found: 'No billing schedule found for this client.',
  schedule_already_exists: 'This client already has a billing schedule.',
  invalid_organisation_id: 'Select a client organisation to onboard.',
  not_consultant_client: 'This organisation is not a consultant end client.',
  not_authorised: 'You do not have permission for this action.',
  forbidden: 'You do not have access to this client.',
  not_found: 'Client or contract not found.',
  cannot_resend_in_status: 'Cannot resend the invitation in the current client status.',
};

/**
 * @param {string|object} error
 * @returns {string}
 */
export const friendlyConsultantClientError = (error) => {
  const code = typeof error === 'string' ? error : error?.error || error?.code;
  if (code && CONSULTANT_CLIENT_ERROR_MESSAGES[code]) return CONSULTANT_CLIENT_ERROR_MESSAGES[code];
  if (typeof error === 'object' && error?.message) return error.message;
  return 'Something went wrong. Please try again.';
};

const PENDING_CONTRACTS_KEY = 'consultantPendingContracts';

export const loadPendingContractsFromSession = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_CONTRACTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const savePendingContractToSession = (orgId, contractId) => {
  if (!orgId || !contractId) return;
  const map = loadPendingContractsFromSession();
  map[orgId] = contractId;
  sessionStorage.setItem(PENDING_CONTRACTS_KEY, JSON.stringify(map));
};

export const removePendingContractFromSession = (orgId) => {
  if (!orgId) return;
  const map = loadPendingContractsFromSession();
  delete map[orgId];
  sessionStorage.setItem(PENDING_CONTRACTS_KEY, JSON.stringify(map));
};

/**
 * @param {string|undefined} planType
 * @param {object} [client]
 * @returns {string}
 */
export const planTypeLabel = (planType, client) => {
  if (planType === 'client_subscription') return 'Client subscription';
  if (planType === 'per_report') return 'Per report';
  if (!planType && client && !client.billingRelationship) return 'Per report (legacy)';
  if (!planType && client?.billingRelationship === 'consultant_billed') return 'Per report';
  return planType || '—';
};

/**
 * Resolve display price for a consultant client row.
 * @param {object} client
 * @param {object|null} schedule
 * @param {number|null} masterPerReportFeeVal
 * @param {string} [currencySymbol='£']
 * @returns {string}
 */
export const resolveClientPriceLabel = (client, schedule, masterPerReportFeeVal, currencySymbol = '£') => {
  if (schedule?.planType === 'per_report' && schedule.perReportFeeVal != null) {
    return `${currencySymbol}${schedule.perReportFeeVal} / report`;
  }
  if (schedule?.unitAmount != null) {
    return `${currencySymbol}${schedule.unitAmount} / period`;
  }
  if (!schedule && resolveClientStatus(client) === 'Active' && masterPerReportFeeVal != null) {
    return `${currencySymbol}${masterPerReportFeeVal} / report (legacy master fee)`;
  }
  return '—';
};

/**
 * Resolve commitment end display for portfolio / detail.
 * @param {object} client
 * @param {object|null} schedule
 * @returns {string}
 */
export const resolveCommitmentEndLabel = (client, schedule) => {
  if (schedule?.commitmentEndDate) return schedule.commitmentEndDate;
  if (client?.subscriptionEndDate) return client.subscriptionEndDate;
  return 'Set by CYF admin';
};

/**
 * Clients under consultant missing a billing schedule row.
 * @param {object[]} clients
 * @param {string} consultantParentId
 * @returns {object[]}
 */
export const clientsMissingSchedule = (clients, consultantParentId) =>
  filterConsultantClients(clients, consultantParentId).filter((c) => !c.consultantClientScheduleId);
